import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Lock, Shield, Eye, KeyRound, Server, FileCheck, Copy, Check,
  Stethoscope, ArrowRight, Sparkles, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import Breadcrumb from '@/components/Breadcrumb';
import JumpToNav from '@/components/JumpToNav';

// One-pager for doctors to share with privacy-conscious patients.
// Plain language, no security jargon, with one technical footnote per layer
// for the rare patient who wants the gory details.

const LAYERS = [
  {
    icon: Lock,
    color: 'emerald',
    headline: 'Your phone number and email are locked at rest.',
    plain:
      "Even if DocSite's database itself were ever copied or leaked, the rows containing your phone number and email would look like random scrambled text — unreadable without a key that DocSite never stores in the same place.",
    technical:
      'Field-level encryption using Fernet (AES-128 in CBC mode with HMAC-SHA256), key derived from a founder passphrase via PBKDF2-HMAC-SHA256.',
  },
  {
    icon: Shield,
    color: 'sky',
    headline: 'Every page you load uses a private connection.',
    plain:
      'Every time you book, message, or load a doctor\'s page, your browser and DocSite establish a private channel that nobody else on the network — your café Wi-Fi, your office router, even your internet provider — can read.',
    technical:
      'TLS 1.3 enforced at the load balancer. HTTP traffic is automatically upgraded; no plain-text fallback is permitted.',
  },
  {
    icon: KeyRound,
    color: 'violet',
    headline: 'Only the doctor you reached out to can see your details.',
    plain:
      'DocSite\'s servers can read your booking and route it, but the doctor\'s authenticated session — and only that session — sees your decrypted contact details. Other doctors on the platform cannot.',
    technical:
      'Per-tenant access scoping at the API layer + JWT-bearer authentication. Cross-doctor leakage is prevented by row-level filters on every read.',
  },
  {
    icon: Eye,
    color: 'amber',
    headline: 'Nobody at DocSite is reading your messages.',
    plain:
      'There is no marketing team, no aggregator partner, and no third-party advertiser with access to your conversations. We don\'t sell patient lists. We don\'t share data with hospital chains. The platform exists to connect you to your doctor — that\'s it.',
    technical:
      'No data-sharing contracts with hospital networks, pharma companies, or aggregators. Patient PII is never used for marketing — including DocSite\'s own marketing.',
  },
  {
    icon: FileCheck,
    color: 'slate',
    headline: 'India\'s privacy law (DPDPA) and NMC rules apply by default.',
    plain:
      'DocSite is built to comply with India\'s Digital Personal Data Protection Act and the National Medical Commission\'s telemedicine and patient-record guidelines. You have the right to ask for a copy of your data, or to ask for it to be deleted.',
    technical:
      'DPDPA 2023 § 5–11 compliance: Notice + Consent + Right to Access/Erasure. NMC Telemedicine Practice Guidelines 2020 alignment for doctor-patient confidentiality.',
  },
  {
    icon: Server,
    color: 'rose',
    headline: 'Your data lives in India.',
    plain:
      'DocSite\'s servers and database are hosted in Indian data centres. Your records are not transferred outside the country, which keeps them inside Indian privacy law and Indian courts.',
    technical:
      'Database, application servers, and backups are hosted in Mumbai region. No cross-border data transfer occurs as part of normal platform operation.',
  },
];

const COPY_FOR_PATIENT = `Hi — quick note on how my online practice handles your data.

I use a platform called DocSite. Your phone number and email are stored encrypted (so even a database leak can't read them), the connection between your browser and the booking page is private end-to-end, and only my authenticated session can see your contact details. DocSite doesn't sell patient lists or share data with aggregators or hospital networks — that's part of why I chose it.

If you'd like the full one-pager: https://docsite.care/security/patient-data

Looking forward to seeing you in clinic.`;

export default function PatientDataProtection() {
  const [copied, setCopied] = useState(false);
  const [showTech, setShowTech] = useState({});

  const copyForPatient = () => {
    const ok = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    };
    // Try modern clipboard first; fall back to a textarea for older browsers / insecure contexts.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(COPY_FOR_PATIENT).then(ok).catch(() => fallbackCopy(ok));
    } else {
      fallbackCopy(ok);
    }
  };

  const fallbackCopy = (onDone) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = COPY_FOR_PATIENT;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      onDone();
    } catch {
      // Last resort: show user the text in a prompt so they can manually copy
      window.prompt('Copy this paragraph to share with your patient:', COPY_FOR_PATIENT);
    }
  };

  const toggleTech = (i) => setShowTech((prev) => ({ ...prev, [i]: !prev[i] }));

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>How Your Patient Data Is Protected on DocSite | A One-Pager for Patients & Doctors</title>
        <meta
          name="description"
          content="Plain-language explainer of how DocSite protects patient phone numbers, emails, and bookings — encryption at rest, private connections, doctor-only access, DPDPA/NMC compliance, India-hosted data. A one-pager doctors can share with their patients."
        />
        <link rel="canonical" href="https://docsite.care/security/patient-data" />
        <meta property="og:title" content="How Your Patient Data Is Protected on DocSite" />
        <meta property="og:description" content="A one-pager doctors can share with privacy-conscious patients — encryption, private connections, doctor-only access, India-hosted, DPDPA-aligned." />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'How Your Patient Data Is Protected on DocSite',
            author: { '@type': 'Organization', name: 'DocSite AI Team' },
            publisher: {
              '@type': 'Organization',
              name: 'DocSite',
              logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo-512.png' },
            },
            datePublished: '2026-04-26',
            dateModified: '2026-04-26',
          })}
        </script>
      </Helmet>

      {/* Hero — left narrative, right "share with patient" panel */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-b border-slate-100">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 md:py-16">
          <Breadcrumb
            items={[
              { label: 'DocSite', to: '/' },
              { label: 'Security' },
              { label: 'Patient Data' },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-3 inline-flex items-center gap-1.5">
                <ShieldCheck size={12} /> Patient privacy, plainly explained
              </p>
              <h1
                className="font-medium text-slate-900 tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.25rem, 3.6vw, 4.25rem)', lineHeight: 1.1 }}
                data-testid="patient-data-h1"
              >
                How your patient data is protected on DocSite.
              </h1>
              <p className="text-slate-600 mt-6 leading-relaxed lg:max-w-[68ch]" style={{ fontSize: 'clamp(1.05rem, 1.15vw, 1.3rem)' }}>
                A one-pager you can share with your patients — written in plain language, with the technical details
                tucked away for the rare patient who wants to see them. No security jargon, no marketing fluff. Just
                what actually happens to a phone number from the moment a patient types it in.
              </p>

              <div className="mt-6 flex flex-wrap gap-3" data-testid="patient-data-cta-row">
                <button
                  onClick={copyForPatient}
                  data-testid="copy-for-patient-btn"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all ${
                    copied
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-900 text-white hover:bg-emerald-600'
                  }`}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied — paste into WhatsApp' : 'Copy a patient-friendly summary'}
                </button>
                <Link
                  to="/for"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                >
                  Back to specialties <ArrowRight size={14} />
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <JumpToNav
                  testId="patient-data-jump-nav"
                  className="!mt-0 !pt-0 !border-0"
                  items={[
                    { icon: AlertTriangle, label: 'Why this matters', href: '#why', color: 'amber' },
                    { icon: Shield, label: 'The 6 layers', href: '#layers', color: 'emerald' },
                    { icon: Stethoscope, label: 'Tell your patient', href: '#share', color: 'sky' },
                    { icon: Sparkles, label: 'Your rights', href: '#rights', color: 'violet' },
                  ]}
                />
              </div>
            </div>

            {/* Right rail — quick-reference card */}
            <aside className="lg:col-span-5 lg:sticky lg:top-24" data-testid="patient-data-rail">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-3">
                  Quick reference
                </p>
                <ul className="space-y-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2.5">
                    <Lock size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Encrypted at rest.</strong> Phone & email locked even from
                      DocSite operators.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Shield size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Private connection.</strong> Browser-to-server traffic
                      can't be read by anyone in between.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <KeyRound size={16} className="text-violet-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Doctor-only access.</strong> Only the specific specialist
                      you reached out to sees your details.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Eye size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">No data sale.</strong> Patient lists are never shared with
                      aggregators, hospital chains, or pharma.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <FileCheck size={16} className="text-slate-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">DPDPA + NMC aligned.</strong> Right to access, right to
                      delete — both supported.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Server size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-slate-900">Hosted in India.</strong> Your data stays inside Indian
                      privacy law and Indian courts.
                    </span>
                  </li>
                </ul>
              </div>
              <p className="text-xs text-slate-400 mt-3 text-center">
                Last reviewed 2026-04-26 by the DocSite AI Team.
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* Why this matters — short, real, blunt */}
      <section id="why" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16 scroll-mt-24" data-testid="patient-data-why">
        <div className="max-w-4xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-700 mb-3">
            Why this even needs explaining
          </p>
          <h2
            className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mb-5"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Most patient bookings online aren't as private as they look.
          </h2>
          <div className="space-y-4 text-base text-slate-700 leading-relaxed">
            <p>
              When a patient books through an aggregator or a hospital chain, their phone number and condition usually
              end up in three places: the platform itself, the platform's marketing partners, and — eventually — a
              call-centre that follows up to upsell tests and packages. The patient gave consent to "see a doctor",
              but ended up giving consent to a marketing pipeline.
            </p>
            <p>
              DocSite was built so that <strong>the doctor</strong>, not a marketing system, owns the patient
              relationship. The six layers below explain how that's technically enforced — in language a patient who
              has never written a line of code can read in 90 seconds.
            </p>
          </div>
        </div>
      </section>

      {/* The 6 layers */}
      <section
        id="layers"
        className="bg-slate-50 border-y border-slate-100 py-12 md:py-16 scroll-mt-24"
        data-testid="patient-data-layers"
      >
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mb-3">
            The 6 protection layers
          </p>
          <h2
            className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mb-8 max-w-3xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            What actually happens to a patient's phone number on DocSite.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {LAYERS.map((layer, i) => {
              const Icon = layer.icon;
              const colorMap = {
                emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                sky: 'bg-sky-50 text-sky-700 border-sky-200',
                violet: 'bg-violet-50 text-violet-700 border-violet-200',
                amber: 'bg-amber-50 text-amber-700 border-amber-200',
                slate: 'bg-slate-100 text-slate-700 border-slate-200',
                rose: 'bg-rose-50 text-rose-700 border-rose-200',
              };
              return (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-200 transition-colors"
                  data-testid={`patient-data-layer-${i}`}
                >
                  <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${colorMap[layer.color]} mb-3`}>
                    <Icon size={20} />
                  </div>
                  <h3
                    className="text-lg font-medium text-slate-900 tracking-tight mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {layer.headline}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{layer.plain}</p>
                  <button
                    onClick={() => toggleTech(i)}
                    className="text-xs text-slate-400 hover:text-slate-600 mt-3 inline-flex items-center gap-1 transition-colors"
                    data-testid={`patient-data-layer-${i}-toggle`}
                  >
                    {showTech[i] ? 'Hide technical detail' : 'Show technical detail'}
                  </button>
                  {showTech[i] && (
                    <p className="text-xs text-slate-500 mt-2 p-3 rounded-lg bg-slate-50 border border-slate-100 font-mono leading-relaxed">
                      {layer.technical}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Share with your patient — the WhatsApp-paste-ready paragraph */}
      <section
        id="share"
        className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16 scroll-mt-24"
        data-testid="patient-data-share"
      >
        <div className="max-w-4xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700 mb-3">
            For doctors — share this with your patient
          </p>
          <h2
            className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mb-5"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            A WhatsApp-ready paragraph you can paste into any patient conversation.
          </h2>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">{COPY_FOR_PATIENT}</pre>
            <button
              onClick={copyForPatient}
              data-testid="copy-for-patient-btn-bottom"
              className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                copied
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-900 text-white hover:bg-sky-600'
              }`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy paragraph'}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Patients tend to trust a doctor who explains <em>how</em> their data is handled — not just <em>that</em>{' '}
            it is. Use this one whenever a patient asks.
          </p>
        </div>
      </section>

      {/* Patient rights — DPDPA + NMC */}
      <section
        id="rights"
        className="bg-slate-50 border-y border-slate-100 py-12 md:py-16 scroll-mt-24"
        data-testid="patient-data-rights"
      >
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="max-w-4xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-violet-700 mb-3">
              Your rights as a patient
            </p>
            <h2
              className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              What you can ask for at any time.
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                {
                  t: 'A copy of your data',
                  d: 'You can ask DocSite (or the doctor you reached out to) for everything they hold about you — bookings, messages, contact details — and get it in a readable format.',
                },
                {
                  t: 'Your data deleted',
                  d: 'You can ask for your records to be removed. Bookings older than the legally required retention window (NMC: 3 years for adults, 21 years for minors) will be deleted on request.',
                },
                {
                  t: 'A correction',
                  d: 'If your phone number, email, or any record about you is wrong, you can ask for it to be corrected. Doctors can do this directly from their dashboard.',
                },
                {
                  t: 'A grievance heard',
                  d: 'If you feel your data has been mishandled, write to privacy@docsite.care. Indian DPDPA gives you the right to escalate to the Data Protection Board if not resolved in 30 days.',
                },
              ].map((r, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h3
                    className="text-base font-medium text-slate-900 tracking-tight mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {r.t}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{r.d}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-6">
              Source: India's Digital Personal Data Protection Act, 2023 (§ 11–13) and the National Medical
              Commission's Telemedicine Practice Guidelines, 2020. DocSite implements both by default.
            </p>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16 text-center" data-testid="patient-data-closing-cta">
        <div className="max-w-3xl mx-auto">
          <h2
            className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Trust isn't a tagline. It's a stack.
          </h2>
          <p className="text-base text-slate-600 mt-4 leading-relaxed">
            Six layers, one outcome — the patient–doctor conversation stays between the two of them. That's the
            entire reason DocSite exists.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/register?as=doctor&from=patient_data_page"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
              data-testid="patient-data-register-cta"
            >
              Start my DocSite practice <ArrowRight size={14} />
            </Link>
            <Link
              to="/privacy"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:border-violet-300 hover:text-violet-700 transition-colors"
            >
              Read the full Privacy Policy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
